import type { MedplumClient } from '@medplum/core';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import { useCallback, useRef } from 'react';
import {
  isAiNoteCheckBotAvailable,
  isAiNoteCheckBotUnavailableError,
  isAiNoteCheckForbiddenError,
  requestAiNoteCheck,
} from '@/lib/aiNoteCheckService';
import { formatMedplumClientError, isMedplumHttpStatus } from '../utils/medplumClientErrors';
import { config } from '@/config';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

/** Post-save checks run immediately after autosave (deterministic bot; no LLM debounce). */
export const AI_NOTE_CHECK_DEBOUNCE_MS = 0;

export interface UseDebouncedAiNoteCheckOptions {
  medplum: MedplumClient;
  enabled?: boolean;
  onRunning?: (questionnaireResponseId: string) => void;
  onCompleted?: (checked: QuestionnaireResponse) => void;
  onSkipped?: () => void;
  onUnavailable?: () => void;
  onFailed?: (message: string) => void;
}

export interface UseDebouncedAiNoteCheckResult {
  scheduleAiNoteCheck: (savedResponse: QuestionnaireResponse) => void;
}

export function useDebouncedAiNoteCheck(
  options: UseDebouncedAiNoteCheckOptions,
): UseDebouncedAiNoteCheckResult {
  const {
    medplum,
    enabled = true,
    onRunning,
    onCompleted,
    onSkipped,
    onUnavailable,
    onFailed,
  } = options;

  const pendingResponseRef = useRef<QuestionnaireResponse | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const rerunAfterInFlightRef = useRef(false);

  const runCheck = useCallback(
    async (savedResponse: QuestionnaireResponse): Promise<void> => {
      const qrId = savedResponse.id;
      if (!qrId || !enabled) return;

      if (inFlightRef.current) {
        rerunAfterInFlightRef.current = true;
        pendingResponseRef.current = savedResponse;
        return inFlightRef.current;
      }

      const runPromise = (async () => {
        try {
          const botAvailable = await isAiNoteCheckBotAvailable(medplum);
          if (!botAvailable) {
            console.warn(
              'AI note check bot is not registered; skipping automatic review. Run `npm run bot:ai-note-check:register` in medplum-server.',
            );
            onUnavailable?.();
            onSkipped?.();
            return;
          }

          onRunning?.(qrId);
          const checked = await requestAiNoteCheck(medplum, qrId, {
            force: false,
            reason: 'post-save',
          });

          if (checked.id === qrId) {
            onCompleted?.(checked);
          } else {
            onSkipped?.();
          }
        } catch (err) {
          if (isAiNoteCheckBotUnavailableError(err)) {
            console.warn(
              'AI note check bot unavailable; skipping automatic review.',
              formatMedplumClientError(err),
            );
            onUnavailable?.();
            onSkipped?.();
            return;
          }
          const base = getUserFacingErrorMessage(err, 'Automatic note check failed.');
          const hintAuth =
            !config.isProduction && (isMedplumHttpStatus(err, 403) || isAiNoteCheckForbiddenError(err))
              ? 'Sign in as the provider who saved this note. If the appointment was opened from chart mock data, reload the appointment from the calendar so the provider participant is correct.'
              : '';
          onFailed?.([base, hintAuth].filter(Boolean).join(' '));
        } finally {
          inFlightRef.current = null;
          if (rerunAfterInFlightRef.current && pendingResponseRef.current?.id) {
            rerunAfterInFlightRef.current = false;
            const next = pendingResponseRef.current;
            pendingResponseRef.current = null;
            void runCheck(next);
          }
        }
      })();

      inFlightRef.current = runPromise;
      return runPromise;
    },
    [enabled, medplum, onCompleted, onFailed, onRunning, onSkipped, onUnavailable],
  );

  const scheduleAiNoteCheck = useCallback(
    (savedResponse: QuestionnaireResponse) => {
      if (!enabled || !savedResponse.id) return;
      if (!inFlightRef.current) {
        onRunning?.(savedResponse.id);
      }
      void runCheck(savedResponse);
    },
    [enabled, onRunning, runCheck],
  );

  return { scheduleAiNoteCheck };
}
